import { defineStore } from 'pinia';
import ICategory from '@/interfaces/ICategory';
import IImage from '@/interfaces/IImage';
import IDescription from '@/interfaces/IDescription';
import { deduplicateImages } from '@/helpers/images/deduplicateImages';

export const useCategoriesStore = defineStore('categories', {
  state: () => ({
    categories: new Array<ICategory>(),
  }),
  getters: {
    getNumberOfCategories(): number {
      return this.categories.length;
    },
    getAllImages(): IImage[] {
      return this.categories.flatMap((category) => [
        ...category.images,
        ...category.description.images,
      ]);
    },
    getNumberOfImages(): number {
      return this.getAllImages.length;
    },
    // New getter to count images by type
    getImageTypeCounts(): Record<string, number> {
      return this.getAllImages.reduce((acc: Record<string, number>, image: IImage) => {
        acc[image.type] = (acc[image.type] || 0) + 1;
        return acc;
      }, {});
    },
    missingMetaTitleCount(): number {
      return this.categories.filter(category => !category.metaTitle).length;
    },
    missingMetaDescriptionCount(): number {
      return this.categories.filter(category => !category.metaDescription).length;
    },
    missingDescriptionCount(): number {
      return this.categories.filter(category => !category.description.descriptionHtml).length;
    },
    missingNameCount(): number {
      return this.categories.filter(category => !category.name || category.name.trim() === '').length;
    },
    missingProductsPathsCount(): number {
      return this.categories.filter(category => !category.productsPaths || category.productsPaths.length === 0).length;
    },
    missingImagesCount(): number {
      return this.categories.filter(category => !category.images || category.images.length === 0).length;
    },
  },

  actions: {
    addCategory(
      pathname: string,
      metaTitle: string,
      metaDescription: string,
      name: string,
      images: IImage[],
      productsPaths: string[],
      description: IDescription
    ) {
      const { dedupedPrimary, dedupedSecondary } = deduplicateImages(images, description.images);
      const newCategory: ICategory = {
        pathname,
        metaTitle,
        metaDescription,
        name,
        images: dedupedPrimary,
        productsPaths,
        description: {
          ...description,
          images: dedupedSecondary,
        },
      };
      this.categories.push(newCategory);
    },
    clearCategoryStore() {
      this.categories = new Array<ICategory>();
    },
    hasUrl(pathname: string) {
      return this.categories.some(
        (categories) => categories.pathname === pathname
      );
    },
    getNameByPathname(pathname: string): string | undefined {
      const category = this.categories.find(
        (category) => category.pathname === pathname
      );
      return category?.name;
    },
    getCategoryNamesByProductPath(productPath: string): string[] {
      return this.categories
        .filter((category) => category.productsPaths.includes(productPath))
        .map((category) => category.name);
    },
    getCategoryPathnamesByProductPath(productPath: string): string[] {
      return this.categories
        .filter((category) => category.productsPaths.includes(productPath))
        .map((category) => category.pathname);
    },
    getCategoryByPathname(pathname: string) {
      return this.categories.find((category) => category.pathname === pathname);
    },
  },
});
